import { Box, Button, Flex, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiOutlinePlus } from "react-icons/hi2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import CreateClientDrawer from "@/pages/clients/components/CreateClientDrawer";
import ClientsToolbar from "@/pages/clients/components/ClientsToolbar";
import ClientsPagination from "@/pages/clients/components/ClientsPagination";
import CompanyRow from "@/pages/clients/components/CompanyRow";
import EditCompanyDrawer from "@/pages/clients/components/EditCompanyDrawer";
import MemberFormDialog from "@/pages/clients/components/MemberFormDialog";
import ConfirmDialog from "@/pages/clients/components/ConfirmDialog";
import { CHANGES_THAT_RESET_PAGE, parseQuery, toSearchParams } from "@/pages/clients/clientsQuery";
import { companyLabel } from "@/pages/clients/companyLabel";
import {
    isCompanyExpanded,
    loadExpandedState,
    resetCollapsedOnSearchChange,
    saveExpandedState,
    toggleCompany,
} from "@/pages/clients/expandedCompanies";

const EmptyState = ({ title, hint, action }) => (
    <VStack w="full" py={16} gap={3}>
        <Text color="white" fontWeight="semibold">{title}</Text>
        {hint && <Text color="text.grey" fontSize="sm">{hint}</Text>}
        {action}
    </VStack>
);

const ClientsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = useMemo(() => parseQuery(searchParams), [searchParams]);

    const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: 20 });
    const [status, setStatus] = useState("loading"); // loading | ready | error
    const reqId = useRef(0);

    const [expandedState, setExpandedState] = useState(loadExpandedState);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editCompany, setEditCompany] = useState(null);
    const [memberDialog, setMemberDialog] = useState(null); // { mode, company, member }
    const [confirm, setConfirm] = useState(null); // { title, body, confirmLabel, run }
    const [confirmBusy, setConfirmBusy] = useState(false);

    const load = useCallback(() => {
        const id = ++reqId.current;
        setStatus((s) => (s === "ready" ? "ready" : "loading"));
        ClientService.getCompanies(query)
            .then((res) => {
                if (id !== reqId.current) return;
                setData(res);
                setStatus("ready");
            })
            .catch(() => {
                if (id !== reqId.current) return;
                setStatus("error");
            });
    }, [query]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        setExpandedState((prev) => {
            const next = resetCollapsedOnSearchChange(prev, query.search);
            if (next !== prev) saveExpandedState(next);
            return next;
        });
    }, [query.search]);

    const patchQuery = (patch) => {
        const next = { ...query, ...patch };
        if (Object.keys(patch).some((k) => CHANGES_THAT_RESET_PAGE.includes(k))) next.page = 1;
        setSearchParams(toSearchParams(next), { replace: true });
    };

    const handleToggleExpand = (company) => {
        setExpandedState((prev) => {
            const next = toggleCompany(prev, company);
            saveExpandedState(next);
            return next;
        });
    };

    // --- mutations -------------------------------------------------------
    const runMutation = async (fn, successMsg) => {
        try {
            await fn();
            if (successMsg) showToast(successMsg, null, "success");
            load();
            return true;
        } catch (err) {
            showToast("Error", err?.response?.data ?? "Action failed", "error");
            return false;
        }
    };

    const askDeactivateCompany = (company) => {
        const owner = company.owner;
        setConfirm({
            title: "Deactivate company",
            body: `Deactivate the owner account ${owner?.firstName} ${owner?.lastName}?\n\n`
                + `This also deactivates every user assigned to ${companyLabel(company)}.`,
            confirmLabel: "Deactivate",
            run: () => ClientService.deactivateCompany(company.id),
            successMsg: "Company deactivated",
        });
    };

    const askDeactivateMember = (member) => {
        if (member.role === "Owner") {
            const company = data.items.find((c) => c.id === member.id || c.owner?.id === member.id);
            askDeactivateCompany(company ?? { id: member.id, owner: member, companyName: "" });
            return;
        }
        setConfirm({
            title: "Deactivate user",
            body: `Deactivate ${member.firstName} ${member.lastName} (${member.email})?\n\n`
                + "Other accounts of the company are not affected.",
            confirmLabel: "Deactivate",
            run: () => ClientService.deactivateMember(member.id),
            successMsg: "User deactivated",
        });
    };

    const handleToggleCompanyActive = (company) => {
        if (company.status === "Active") askDeactivateCompany(company);
        else runMutation(() => ClientService.activateCompany(company.id), "Company owner activated");
    };

    const handleToggleMember = (member) => {
        if (member.isActive) askDeactivateMember(member);
        else runMutation(() => ClientService.activateMember(member.id), "User activated");
    };

    const handleResendInvitation = (member) =>
        runMutation(() => ClientService.resendInvitation(member.id), "Invitation sent");

    const runConfirm = async () => {
        if (!confirm) return;
        setConfirmBusy(true);
        const ok = await runMutation(confirm.run, confirm.successMsg);
        setConfirmBusy(false);
        if (ok) setConfirm(null);
    };

    // --- render ---------------------------------------------------------
    const companies = data.items;
    const hasFilters = query.search
        || query.companyStatus !== "All" || query.userStatus !== "All" || query.role !== "All";

    return (
        <VStack w="full" rowGap={5} align="stretch">
            <Flex justify="space-between" align="center" w="full" gap={3} flexWrap="wrap">
                <Text color="white" fontSize={24} fontWeight="semibold">Clients</Text>
                <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                    <Icon fontSize={46}><HiOutlinePlus /></Icon>
                    <Text>Add Client</Text>
                </Button>
            </Flex>

            <ClientsToolbar query={query} onChange={patchQuery} />

            {status === "loading" && companies.length === 0 ? (
                <Flex w="full" py={20} align="center" justify="center">
                    <Spinner size="xl" color="white" />
                </Flex>
            ) : status === "error" ? (
                <EmptyState
                    title="Couldn’t load clients"
                    hint="Something went wrong while contacting the server."
                    action={<Button variant="outlined" onClick={load}>Retry</Button>}
                />
            ) : companies.length === 0 ? (
                hasFilters ? (
                    <EmptyState title="No clients or users match your search." />
                ) : (
                    <EmptyState
                        title="No clients yet."
                        action={
                            <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                                <Icon fontSize={46}><HiOutlinePlus /></Icon>
                                <Text>Add Client</Text>
                            </Button>
                        }
                    />
                )
            ) : (
                <VStack w="full" gap={3} align="stretch" opacity={status === "loading" ? 0.6 : 1} transition="opacity 0.15s">
                    {companies.map((company) => (
                        <CompanyRow
                            key={company.id}
                            company={company}
                            expanded={isCompanyExpanded(expandedState, company)}
                            onToggleExpand={handleToggleExpand}
                            onEdit={setEditCompany}
                            onToggleActive={handleToggleCompanyActive}
                            onEditMember={(member) =>
                                member.role === "Owner"
                                    ? setEditCompany(company)
                                    : setMemberDialog({ mode: "edit", company, member })}
                            onToggleMember={handleToggleMember}
                            onResendInvitation={handleResendInvitation}
                            onAddMember={(c) => setMemberDialog({ mode: "add", company: c })}
                        />
                    ))}
                </VStack>
            )}

            {companies.length > 0 && (
                <ClientsPagination
                    page={data.page}
                    pageSize={data.pageSize}
                    totalCount={data.totalCount}
                    onPageChange={(p) => patchQuery({ page: p })}
                    onPageSizeChange={(size) => patchQuery({ pageSize: size })}
                />
            )}

            <CreateClientDrawer
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onCreated={load}
            />
            <EditCompanyDrawer
                isOpen={!!editCompany}
                company={editCompany}
                onClose={() => setEditCompany(null)}
                onSaved={load}
            />
            <MemberFormDialog
                open={!!memberDialog}
                mode={memberDialog?.mode}
                company={memberDialog?.company}
                member={memberDialog?.member}
                onClose={() => setMemberDialog(null)}
                onSaved={load}
            />
            <ConfirmDialog
                open={!!confirm}
                title={confirm?.title}
                body={confirm?.body}
                confirmLabel={confirm?.confirmLabel}
                isLoading={confirmBusy}
                onCancel={() => !confirmBusy && setConfirm(null)}
                onConfirm={runConfirm}
            />
        </VStack>
    );
};

export default ClientsPage;
